import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Alert,
  AlertTitle,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import DeleteConfirmModal from "../AlertComponents/DeleteConfirmModal";
import {
  cancelBillingSubscription,
  fetchBillingPlans,
  fetchBillingSubscription,
  getBillingErrorMessage,
  isPaidPlan,
  openBillingPortal,
  resumeBillingSubscription,
  startBillingCheckout,
} from "../../utils/billingApi";

const BODY_TEXT = "#0f172a";
const SUBTEXT = "#334155";
const BORDER = "#e2e8f0";
const BRAND = "#047857";
const CARD_BG = "#f8fafc";
const UPGRADE_PLAN_ID = "organizer_intro";
const RENEWAL_PLAN_ID = "organizer_standard";

function formatMonthlyPrice(cents) {
  if (cents == null || Number.isNaN(cents)) return "";
  return `$${Math.round(cents / 100)}`;
}

function formatDate(iso) {
  if (!iso) return "";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

const ACTION_BUTTON_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 15,
  borderRadius: "10px",
  minHeight: 44,
  px: 2.5,
};

function PlanMetaLine({ children, sx }) {
  return (
    <Typography
      sx={{
        fontSize: { xs: 15, sm: 16 },
        color: BODY_TEXT,
        lineHeight: 1.5,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function statusChip(pastDue, pendingCancel, paid, periodEndLabel) {
  if (pastDue) {
    return <Chip label="Payment issue" size="small" color="warning" sx={{ fontWeight: 700 }} />;
  }
  if (pendingCancel) {
    return (
      <Chip
        label={periodEndLabel ? `Ends ${periodEndLabel}` : "Ending soon"}
        size="small"
        sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 700 }}
      />
    );
  }
  if (paid) {
    return (
      <Chip
        label="Active"
        size="small"
        sx={{ bgcolor: "#d1fae5", color: "#065f46", fontWeight: 700 }}
      />
    );
  }
  return (
    <Chip
      label="Free"
      size="small"
      sx={{ bgcolor: "#f1f5f9", color: SUBTEXT, fontWeight: 700 }}
    />
  );
}

export default function BillingSection({ canSubscribe = true }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const [planPricing, setPlanPricing] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  const loadBilling = useCallback(async (sessionId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchBillingSubscription(sessionId);
      setBilling(data);
      return data;
    } catch (err) {
      setError(getBillingErrorMessage(err, { action: "load your billing details" }));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const returnedFromCheckout = searchParams.get("billing") === "success";

    const loadPlans = async () => {
      try {
        const plansData = await fetchBillingPlans();
        const upgradePlan = plansData?.plans?.find((p) => p.id === UPGRADE_PLAN_ID);
        const renewalPlan = plansData?.plans?.find((p) => p.id === RENEWAL_PLAN_ID);
        setPlanPricing({
          upgradeCents: upgradePlan?.price_cents ?? plansData?.organizer?.intro_price_cents,
          renewalCents: renewalPlan?.price_cents ?? plansData?.organizer?.standard_price_cents,
        });
      } catch (err) {
        // Pricing display is optional; checkout still works server-side.
      }
    };

    loadPlans();
    loadBilling(returnedFromCheckout ? sessionId : null).then((data) => {
      if (!returnedFromCheckout) return;

      searchParams.delete("billing");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });

      if (!data) return;
      // Only claim success when the plan actually changed. Stripe redirects here
      // before the webhook always lands, so a paid checkout can briefly still
      // read as the free plan.
      if (isPaidPlan(data.plan)) {
        setNotice({
          severity: "success",
          message: `You're on the ${data.display_name} plan. Thank you for supporting CivixThread.`,
        });
      } else {
        setNotice({
          severity: "info",
          title: "Payment received",
          message:
            "We're still finishing setup on our side. This usually takes a few seconds — refresh to check again.",
          showRefresh: true,
        });
      }
    });
    // Runs once on mount: the checkout params are consumed here and then cleared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpgrade = async (plan) => {
    setActionLoading(true);
    setError(null);
    try {
      const { checkout_url: checkoutUrl } = await startBillingCheckout(plan);
      if (checkoutUrl) window.location.href = checkoutUrl;
    } catch (err) {
      const httpStatus = err?.response?.status;
      const code = err?.response?.data?.code;

      if (httpStatus === 409 && code === "already_subscribed") {
        try {
          const { portal_url: portalUrl } = await openBillingPortal();
          if (portalUrl) {
            window.location.href = portalUrl;
            return;
          }
        } catch (portalErr) {
          setError(getBillingErrorMessage(portalErr, { action: "open billing" }));
          return;
        }
      }

      setError(getBillingErrorMessage(err, { action: "start checkout" }));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePortal = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const { portal_url: portalUrl } = await openBillingPortal();
      if (portalUrl) window.location.href = portalUrl;
    } catch (err) {
      setError(getBillingErrorMessage(err, { action: "open billing" }));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const data = await cancelBillingSubscription();
      setBilling(data);
      const endsOn = formatDate(data.current_period_end);
      setNotice({
        severity: "info",
        title: "Plan cancelled",
        message: endsOn
          ? `Your plan ends on ${endsOn}. You keep every paid feature until then, and you can resume any time before that date.`
          : "Your plan will end when the current billing period closes. You keep every paid feature until then.",
      });
    } catch (err) {
      setError(getBillingErrorMessage(err, { action: "cancel your plan" }));
    } finally {
      // Always close: the error alert renders behind the dialog otherwise.
      setCancelOpen(false);
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const data = await resumeBillingSubscription();
      setBilling(data);
      const renewsOn = formatDate(data.current_period_end);
      setNotice({
        severity: "success",
        message: renewsOn
          ? `Your plan is active again and renews on ${renewsOn}.`
          : "Your plan is active again.",
      });
    } catch (err) {
      setError(getBillingErrorMessage(err, { action: "resume your plan" }));
    } finally {
      setActionLoading(false);
    }
  };

  if (!canSubscribe) {
    return null;
  }

  if (loading) {
    return (
      <Box
        id="billing"
        sx={{ scrollMarginTop: "88px", display: "flex", justifyContent: "center", py: 3 }}
      >
        <CircularProgress size={28} sx={{ color: BRAND }} />
      </Box>
    );
  }

  const paid = isPaidPlan(billing?.plan);
  const pendingCancel = paid && !!billing?.cancel_at_period_end;
  const pastDue = paid && billing?.plan_status === "past_due";
  const canManage = billing?.can_manage_billing !== false;
  const upgradePriceLabel = formatMonthlyPrice(planPricing?.upgradeCents) || "$99";
  const renewalPriceLabel = formatMonthlyPrice(planPricing?.renewalCents) || "$149";
  const introIsFree = planPricing?.upgradeCents === 0;
  const introPitch = introIsFree
    ? `Free for the first year, then ${renewalPriceLabel}/mo.`
    : `${upgradePriceLabel}/mo for the first year, then ${renewalPriceLabel}/mo.`;
  const upgradeLabel = introIsFree ? "Start your free year" : `Upgrade — ${upgradePriceLabel}/mo`;
  const periodEndLabel = formatDate(billing?.current_period_end);

  return (
    <Box id="billing" sx={{ scrollMarginTop: "88px" }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: BODY_TEXT, mb: 1, fontSize: { xs: 20, sm: 22 } }}
      >
        Billing &amp; plan
      </Typography>
      <Typography sx={{ fontSize: { xs: 15, sm: 16 }, color: BODY_TEXT, mb: 3, lineHeight: 1.5 }}>
        Manage your organization&apos;s CivixThread plan and invoices.
      </Typography>

      {notice && (
        <Alert
          severity={notice.severity}
          sx={{ mb: 2 }}
          onClose={() => setNotice(null)}
          action={
            notice.showRefresh ? (
              <Button
                size="small"
                onClick={() => loadBilling(null)}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Refresh
              </Button>
            ) : undefined
          }
        >
          {notice.title && <AlertTitle sx={{ fontWeight: 700 }}>{notice.title}</AlertTitle>}
          {notice.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {billing?.sync_warning && !notice && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>We couldn&apos;t confirm that payment</AlertTitle>
          Your plan has not changed. If you were charged, email support@civixthread.com and we
          will sort it out.
        </Alert>
      )}
      {pastDue && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            canManage ? (
              <Button
                size="small"
                onClick={handlePortal}
                disabled={actionLoading}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Update payment
              </Button>
            ) : undefined
          }
        >
          <AlertTitle sx={{ fontWeight: 700 }}>Your last payment didn&apos;t go through</AlertTitle>
          Update your payment method to keep your paid features.
        </Alert>
      )}

      <Box
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 2,
          p: { xs: 2.5, sm: 3 },
          bgcolor: CARD_BG,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "flex-start" },
            justifyContent: "space-between",
            gap: { xs: 2.5, sm: 3 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.25,
                mb: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  color: BODY_TEXT,
                  fontSize: { xs: 22, sm: 26 },
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                }}
              >
                {billing?.display_name || "Civic Starter"}
              </Typography>
              {statusChip(pastDue, pendingCancel, paid, periodEndLabel)}
            </Box>

            {!paid && <PlanMetaLine sx={{ fontWeight: 600 }}>{introPitch}</PlanMetaLine>}

            {paid && billing?.price_locked_until && (
              <PlanMetaLine>
                Intro price held until{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {formatDate(billing.price_locked_until)}
                </Box>
              </PlanMetaLine>
            )}

            {periodEndLabel && (
              <PlanMetaLine>
                {pendingCancel ? "Access ends" : "Renews"} on{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {periodEndLabel}
                </Box>
              </PlanMetaLine>
            )}

            {!paid && (
              <Typography sx={{ fontSize: 15, color: SUBTEXT, mt: 1, lineHeight: 1.5 }}>
                Includes 1 active campaign and 1 published event per month.
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "stretch", sm: "flex-end" },
              gap: 1,
              flexShrink: 0,
              minWidth: { sm: 200 },
            }}
          >
            {!paid && (
              <Button
                variant="contained"
                color="primary"
                disabled={actionLoading || !canManage}
                onClick={() => handleUpgrade(UPGRADE_PLAN_ID)}
                sx={{ ...ACTION_BUTTON_SX, width: { xs: "100%", sm: "auto" } }}
              >
                {actionLoading ? "Redirecting..." : upgradeLabel}
              </Button>
            )}

            {pendingCancel && (
              <Button
                variant="contained"
                color="primary"
                disabled={actionLoading || !canManage}
                onClick={handleResume}
                sx={{ ...ACTION_BUTTON_SX, width: { xs: "100%", sm: "auto" } }}
              >
                {actionLoading ? "Working..." : "Resume plan"}
              </Button>
            )}

            {paid && (
              <Button
                variant="outlined"
                disabled={actionLoading || !canManage}
                onClick={handlePortal}
                sx={{
                  ...ACTION_BUTTON_SX,
                  width: { xs: "100%", sm: "auto" },
                  color: BODY_TEXT,
                  borderColor: BORDER,
                  bgcolor: "#fff",
                  "&:hover": { borderColor: BODY_TEXT, bgcolor: "#fff" },
                }}
              >
                Payment &amp; invoices
              </Button>
            )}

            {paid && !pendingCancel && (
              <Button
                variant="text"
                disabled={actionLoading || !canManage}
                onClick={() => setCancelOpen(true)}
                sx={{
                  ...ACTION_BUTTON_SX,
                  color: "#b91c1c",
                  width: { xs: "100%", sm: "auto" },
                  "&:hover": { bgcolor: "rgba(185, 28, 28, 0.06)" },
                }}
              >
                Cancel plan
              </Button>
            )}
          </Box>
        </Box>

        {paid && !canManage && (
          <Typography
            sx={{
              fontSize: 14,
              color: SUBTEXT,
              mt: 2,
              pt: 2,
              borderTop: `1px solid ${BORDER}`,
              lineHeight: 1.5,
            }}
          >
            Only the billing owner or an admin on this account can change the plan.
          </Typography>
        )}
      </Box>

      <DeleteConfirmModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={handleCancel}
        loading={actionLoading}
        title="Cancel your plan?"
        cancelLabel="Keep plan"
        confirmLabel="Cancel plan"
        description={
          periodEndLabel
            ? `You'll keep every paid feature until ${periodEndLabel}, then drop to Civic Starter (1 active campaign, 1 event per month). You can resume any time before that date.`
            : "You'll keep every paid feature until the current billing period ends, then drop to Civic Starter (1 active campaign, 1 event per month)."
        }
      />
    </Box>
  );
}
